import { apiFetch, apiGet, apiPost } from './api';

// Legacy CP query builder — kept for fetchClosedReferrals, which still
// hits /api/carrier-portal/closed-referrals/ (the closed view hasn't
// migrated to v1 yet because v1 doesn't model the legacy ref_status
// closed-state set).
function _buildLegacyCpQuery(params = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.append('page', params.page);
  if (params.page_size) qs.append('page_size', params.page_size);
  if (params.ordering) qs.append('ordering', params.ordering);
  if (params.client_id) qs.append('client_id', params.client_id);
  if (params.tpa_id) qs.append('tpa_id', params.tpa_id);
  if (params.referral_type) qs.append('referral_type', params.referral_type);
  if (params.status) qs.append('status', params.status);
  if (params.assigned_to) qs.append('assigned_to', params.assigned_to);
  if (params.search) qs.append('search', params.search);
  if (params.closed === true) qs.append('closed', 'true');
  if (params.published_from) qs.append('published_from', params.published_from);
  if (params.published_to) qs.append('published_to', params.published_to);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// Map CP-side filter param names to the v1 endpoint's App-DB-aligned names.
// FE state still uses the legacy names everywhere (tpa_id) so the translation
// lives in this service rather than threaded through useReferralList + the
// filter UI.
//
// NOTE: referral_type (Claim/Member/Provider) passes through unchanged — it is
// the CP's own v1 filter. It must NOT be mapped to model_type, which on v1 is
// the Carrier Engagement line-of-business dimension (Medical/Pharmacy/Dental)
// with a strict allow-list; sending "claim" there is a 400.
const V1_FILTER_NAME_MAP = {
  tpa_id: 'partner_id',
};

// Filters that pass through to v1 unchanged.
const V1_PASSTHROUGH_FILTERS = new Set([
  'client_id',
  'referral_type',
  'aging_out',
  'assigned_to',
  'workflow_step',
  'is_question',
  // Published Date range — inclusive ISO YYYY-MM-DD bounds, sent verbatim.
  'published_from',
  'published_to',
]);

function _buildV1OpenReferralsQuery({ page_size, search, ordering, ...filters } = {}) {
  const qs = new URLSearchParams();
  if (page_size) qs.set('limit', String(page_size));
  if (search) qs.set('search', search);
  if (ordering) qs.set('ordering', ordering);
  // OM/non-demo scoping is enforced at migration time — no need to send these as query params.
  Object.entries(filters).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    if (V1_FILTER_NAME_MAP[key]) {
      qs.set(V1_FILTER_NAME_MAP[key], String(value));
    } else if (V1_PASSTHROUGH_FILTERS.has(key)) {
      qs.set(key, String(value));
    }
    // Dropped: page (cursor-paginated), workflow_step (deferred to PRODUCT-145 follow-up PR).
    // status is supported on the backend; CP filter UI is unwired so it's a no-op from here.
  });
  return qs;
}

/**
 * Fetch referrals for carrier portal with optional filtering and pagination.
 *
 * Backed by the portal-agnostic /api/v1/referrals/ endpoint. Translates
 * the legacy CP request shape (page/page_size/ordering, tpa_id,
 * referral_type, ...) to the v1 shape (limit, cursor, partner_id,
 * model_type, ...) and adapts the response back to the legacy
 * {count, next, previous, results} shape so useReferralList /
 * ReferralTable consumers don't need to change for this rewire.
 *
 * @returns {Promise<{count:number, next:string|null, previous:null, results:Array}>}
 */
export const fetchReferrals = async (params = {}) => {
  const qs = _buildV1OpenReferralsQuery(params);
  const data = await apiGet(`/api/v1/referrals/?${qs.toString()}`);
  return {
    count: data.total_records,
    next: data.next_page,
    previous: null,
    results: data.results,
  };
};

/**
 * Fetch closed referrals for carrier portal with optional filtering, sorting, and pagination.
 * Uses the same params and response shape as fetchReferrals.
 * @param {Object} params - Query parameters (see fetchReferrals for details)
 * @returns {Promise<Object>} Response with results array and pagination info
 */
export const fetchClosedReferrals = async (params = {}) =>
  apiGet(`/api/carrier-portal/closed-referrals/${_buildLegacyCpQuery(params)}`);

// Open export: same v1 query builder as the grid so filter vocabulary matches.
// page_size is omitted — no limit is sent, returning all matching rows.
export const exportOpenReferrals = async (params = {}) => {
  const qs = _buildV1OpenReferralsQuery(params);
  return apiFetch(`/api/v1/export/referrals/?${qs.toString()}`);
};

// Closed export: same legacy CP query builder as fetchClosedReferrals.
// closed=true is always appended; callers do not need to include it in params.
export const exportClosedReferrals = async (params = {}) => {
  return apiFetch(`/api/v1/export/referrals/${_buildLegacyCpQuery({ ...params, closed: true })}`);
};


/**
 * Fetch the distinct filter values (clients, TPAs, referral types) for the
 * authenticated user's open referrals. Response shape:
 *   { clients: [...], tpa_carriers: [...], referral_types: [...] }
 * Each list is sorted by label ascending.
 * @returns {Promise<Object>}
 */
export const fetchReferralFilterOptions = async () =>
  apiGet('/api/carrier-portal/referrals/filter-options/');

/**
 * Closed-referrals variant of fetchReferralFilterOptions.
 * @returns {Promise<Object>}
 */
export const fetchClosedReferralFilterOptions = async () =>
  apiGet('/api/carrier-portal/closed-referrals/filter-options/');

/**
 * Fetch detail fields for a single referral (summary, reason, plan of action, codes)
 * @param {number} refId - Legacy referral ID
 * @returns {Promise<Object>} Response with referral detail fields
 */
export async function fetchReferralDetail(refId) {
  return apiGet(`/api/carrier-portal/referrals/${refId}/`);
}

/**
 * Fetch activity list for a specific referral.
 *
 * Backend envelope:
 *   {
 *     results: [...activities],
 *     current_workflow_step: "OPEN_QUESTIONS",
 *     visited_workflow_steps: ["ACKNOWLEDGE", "PROVIDE_INITIAL", "OPEN_QUESTIONS"]
 *   }
 *
 * `visited_workflow_steps` drives the drawer-header timeline so skipped steps
 * render as not-traversed instead of misleadingly completed.
 *
 * @param {number} refId - Legacy referral ID
 * @returns {Promise<{activities: Array, currentWorkflowStep: string, visitedWorkflowSteps: string[]}>}
 */
export const fetchActivities = async (refId, { includeInternalComments = false } = {}) => {
  const qs = includeInternalComments ? '?include_internal_comments=true' : '';
  const response = await apiGet(`/api/carrier-portal/referrals/${refId}/activities/${qs}`);
  // Defensive: be tolerant if an older backend (or a test mock) still returns
  // a bare array. New shape always has `results`.
  if (Array.isArray(response)) {
    return {
      activities: response,
      currentWorkflowStep: 'ACKNOWLEDGE',
      visitedWorkflowSteps: ['ACKNOWLEDGE'],
      latestStatus: null,
    };
  }
  return {
    activities: response.results ?? [],
    currentWorkflowStep: response.current_workflow_step ?? 'ACKNOWLEDGE',
    visitedWorkflowSteps: response.visited_workflow_steps ?? ['ACKNOWLEDGE'],
    latestStatus: response.latest_status ?? null,
  };
};

/**
 * Create a new activity (comment) on a referral.
 * The backend derives `party` from the requesting user's active organization,
 * so the caller does not (and cannot) send it. Any other writable field on the
 * ActivityCreateSerializer (comment, show_external, reason_code, action_code,
 * etc.) is forwarded verbatim.
 * @param {number} refId - Legacy referral ID
 * @param {Object} payload - Request body
 * @returns {Promise<Object>} The created activity
 */
export const postActivity = async (refId, payload) =>
  apiPost(`/api/carrier-portal/referrals/${refId}/activities/`, payload);

/**
 * Fetch claim-line detail rows for a Claim-type referral.
 *
 * Backend returns: { results: [ { ...18 fields per claim line... } ], count: N }
 * Dates are pre-formatted MM/DD/YYYY by the backend serializer.
 *
 * @param {number} refId - Legacy referral ID
 * @returns {Promise<{results: Array, count: number}>}
 */
export const fetchClaimDetails = async (refId) =>
  apiGet(`/api/carrier-portal/referrals/${refId}/claim-details/`);

export const fetchWorkflowSteps = async () => apiGet('/api/carrier-portal/workflow-steps/');

/**
 * Fetch per-step workflow counts (total + overdue) for the workflow bar.
 * Server-computed in SQL over the user's full filtered dataset — not just
 * the current page. Accepts the same filters as fetchReferrals; the backend
 * ignores any workflow_step filter so the bar shows all-step totals.
 *
 * @param {Object} params - Same filter params as fetchReferrals (no pagination)
 * @returns {Promise<{total_count:number, steps:Array<{key,label,count,overdue_count}>}>}
 */
export const fetchWorkflowCounts = async (params = {}) => {
  const qs = _buildV1OpenReferralsQuery(params);
  return apiGet(`/api/v1/workflow-counts/?${qs.toString()}`);
};

/**
 * Fetch claim_tracking rows for a Provider-type referral. Powers the
 * Provider Claims tab in the Referral Drawer.
 *
 * Backend returns an envelope:
 *   { results: [{ claim_id, paid_date }, ...], count: N }
 *
 * 404 is returned for missing, unauthorized, or non-Provider referrals.
 *
 * @param {number} refId - Legacy referral ID
 * @returns {Promise<{results: Array, count: number}>}
 */
export const fetchProviderClaims = async (refId) =>
  apiGet(`/api/carrier-portal/referrals/${refId}/provider-claims/`);

/**
 * Fetch the reason hierarchy (universal -> primary -> secondary) for an organization.
 *
 * Returns the raw nested array straight from the backend — bucketing into
 * status/reason dropdowns happens in the consuming component.
 *
 * @param {number|null} organizationId - Integer PK of the carrier org, or null to fetch the org-agnostic tree
 * @returns {Promise<Array>} Nested reason hierarchy
 */
/**
 * Fetch the SL-disposition cascade options (responses, reasons, actions,
 * required/locked rules, defaults). Single source of truth — the frontend
 * derives all cascade behaviour from this payload rather than hardcoding the
 * SL vocabulary. See ReasonService.get_sl_disposition_options.
 *
 * @returns {Promise<Object>} The options payload.
 */
export const fetchSlDispositionOptions = async () =>
  apiGet('/api/v1/sl-disposition-options/');

/**
 * Create an SL-disposition activity on a referral (Add Comment & Update SL
 * Response). POSTs on the portal-agnostic v1 surface deliberately — the
 * carrier-portal referrals surface is being deprecated. The single
 * reason_code carries the response; party is derived server-side.
 *
 * @param {number} refId - Legacy referral ID
 * @param {Object} payload - { reason_code, action_code, comment, show_external,
 *                             needs_reply, replaces }
 * @returns {Promise<Object>} The created activity
 */
export const postSlDisposition = async (refId, payload) =>
  apiPost(`/api/v1/referrals/${refId}/activities/`, payload);

/**
 * Mark all new carrier comments on a referral thread as read (thread-scoped).
 * SL-employee only. Returns how many dots were cleared.
 *
 * @param {number} refId - Legacy referral ID
 * @returns {Promise<{cleared: number}>}
 */
export const markActivityAsRead = async (refId) =>
  apiPost(`/api/carrier-portal/referrals/${refId}/activities/mark-read/`);

// Build the org_id / legacy_tpa_id query string shared by the reason
// endpoints. Prefer legacy_tpa_id when supplied — used by SL employees
// viewing a carrier referral so they get the carrier's hierarchy (not SL's).
const reasonScopeQuery = (organizationId, legacyTpaId) => {
  // eslint-disable-next-line eqeqeq -- intentional null/undefined nullish check
  if (legacyTpaId != null) {
    return `?legacy_tpa_id=${encodeURIComponent(legacyTpaId)}`;
  }
  // eslint-disable-next-line eqeqeq -- intentional null/undefined nullish check
  if (organizationId != null) {
    return `?organization_id=${encodeURIComponent(organizationId)}`;
  }
  return '';
};

// Carrier-side Status→Reason cascade for the comment modal. The backend
// applies the 2-tier display projection (Status = level above the leaf,
// Reason = the leaf) and HCSC detection, so the frontend just renders the
// returned options. Reason option `value`s are the leaf reason_code to POST.
// Shape: { is_hcsc, statuses:[{value,label}], reasons_by_status:{},
//          status_id_by_reason_id:{} }.
export const fetchStatusReasonOptions = async (organizationId, { legacyTpaId } = {}) =>
  apiGet(
    `/api/carrier-portal/reasons/status-options/${reasonScopeQuery(organizationId, legacyTpaId)}`,
  );
